<script>
import { dynamicTime } from 'shared/helpers/timeHelper';

export default {
  props: {
    sourceAttribution: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    hasSourceData() {
      return this.originalSource || this.lastSource;
    },
    originalSource() {
      return this.sourceAttribution?.original;
    },
    lastSource() {
      return this.sourceAttribution?.last;
    },
    showLastSource() {
      if (!this.lastSource || !this.originalSource) return false;
      // Mostrar solo si son diferentes
      return this.lastSource.id !== this.originalSource.id;
    },
    originalSourceDisplay() {
      return this.getSourceDisplay(this.originalSource);
    },
    lastSourceDisplay() {
      return this.getSourceDisplay(this.lastSource);
    },
  },
  methods: {
    dynamicTime,
    getSourceDisplay(source) {
      if (!source) return null;

      if (source.type === 'organic') {
        return {
          text: this.$t('CONTACT_PANEL.SOURCE.ORGANIC_TRAFFIC'),
          iconClass: 'i-lucide-users',
          platform: this.$t('CONTACT_PANEL.SOURCE.ORGANIC'),
          color: 'text-n-slate-11',
        };
      }

      return {
        text: source.headline || this.truncateUrl(source.url) || this.$t('CONTACT_PANEL.SOURCE.PAID_CAMPAIGN'),
        iconClass: this.getPlatformIconClass(source.platform),
        platform: this.getPlatformName(source.platform),
        url: source.url,
        color: this.getPlatformColor(source.platform),
      };
    },
    getPlatformIconClass(platform) {
      const icons = {
        instagram: 'i-lucide-instagram',
        facebook: 'i-lucide-facebook',
        whatsapp_direct: 'i-lucide-message-circle',
        whatsapp_business: 'i-lucide-message-circle',
      };
      return icons[platform] || 'i-lucide-megaphone';
    },
    getPlatformName(platform) {
      const names = {
        instagram: 'Instagram',
        facebook: 'Facebook',
        whatsapp_direct: 'WhatsApp',
        whatsapp_business: 'WhatsApp Business',
      };
      return names[platform] || 'Campaign';
    },
    getPlatformColor(platform) {
      const colors = {
        instagram: 'text-pink-11',
        facebook: 'text-blue-11',
        whatsapp_direct: 'text-green-11',
        whatsapp_business: 'text-green-11',
      };
      return colors[platform] || 'text-n-slate-11';
    },
    truncateUrl(url) {
      if (!url) return '';
      try {
        const urlObj = new URL(url);
        const truncatedPath = urlObj.pathname.length > 30 
          ? urlObj.pathname.substring(0, 30) + '...'
          : urlObj.pathname;
        return urlObj.hostname + truncatedPath;
      } catch {
        return url.length > 50 ? url.substring(0, 50) + '...' : url;
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return '';
      return this.dynamicTime(timestamp);
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full gap-2 pt-4 mt-4 border-t border-n-slate-6">
    <!-- Title -->
    <div class="flex items-center gap-2 mb-1">
      <span class="i-lucide-chart-spline size-4 text-n-slate-11 flex-shrink-0" />
      <h4 class="m-0 text-sm font-medium text-n-slate-12">
        {{ $t('CONTACT_PANEL.SOURCE.TITLE') }}
      </h4>
    </div>

    <!-- Empty State -->
    <div v-if="!hasSourceData" class="flex items-start gap-2 py-2">
      <span class="i-lucide-info size-[14px] flex-shrink-0 mt-0.5 text-n-slate-9" />
      <p class="m-0 text-sm text-n-slate-10 italic">
        {{ $t('CONTACT_PANEL.SOURCE.NO_DATA') }}
      </p>
    </div>

    <!-- Original Source -->
    <div v-if="hasSourceData && originalSource" class="flex flex-col w-full gap-1">
      <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
        {{ $t('CONTACT_PANEL.SOURCE.ORIGINAL') }}
      </span>
      <div class="flex items-start gap-2 w-full">
        <span
          class="size-[14px] flex-shrink-0 mt-0.5"
          :class="[originalSourceDisplay.iconClass, originalSourceDisplay.color]"
        />
        <div class="flex-1 min-w-0">
          <p class="m-0 text-sm text-n-slate-12 break-words">
            {{ originalSourceDisplay.text }}
          </p>
          <div class="flex items-center gap-2 mt-0.5 text-xs text-n-slate-10">
            <span>{{ originalSourceDisplay.platform }}</span>
            <span>•</span>
            <span>{{ formatDate(originalSource.captured_at) }}</span>
          </div>
          <a
            v-if="originalSourceDisplay.url"
            :href="originalSourceDisplay.url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 mt-1 text-xs text-blue-10 hover:text-blue-11 hover:underline"
          >
            <span class="truncate max-w-xs">{{ truncateUrl(originalSourceDisplay.url) }}</span>
            <span class="i-lucide-external-link text-xs" />
          </a>
        </div>
      </div>
    </div>

    <!-- Last Source (if different) -->
    <div v-if="hasSourceData && showLastSource" class="flex flex-col w-full gap-1 mt-2">
      <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
        {{ $t('CONTACT_PANEL.SOURCE.LAST') }}
      </span>
      <div class="flex items-start gap-2 w-full">
        <span
          class="size-[14px] flex-shrink-0 mt-0.5"
          :class="[lastSourceDisplay.iconClass, lastSourceDisplay.color]"
        />
        <div class="flex-1 min-w-0">
          <p class="m-0 text-sm text-n-slate-12 break-words">
            {{ lastSourceDisplay.text }}
          </p>
          <div class="flex items-center gap-2 mt-0.5 text-xs text-n-slate-10">
            <span>{{ lastSourceDisplay.platform }}</span>
            <span>•</span>
            <span>{{ formatDate(lastSource.captured_at) }}</span>
          </div>
          <a
            v-if="lastSourceDisplay.url"
            :href="lastSourceDisplay.url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 mt-1 text-xs text-blue-10 hover:text-blue-11 hover:underline"
          >
            <span class="truncate max-w-xs">{{ truncateUrl(lastSourceDisplay.url) }}</span>
            <span class="i-lucide-external-link text-xs" />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>
